import { useEffect, useMemo, useState } from "react";
import { FaDownload, FaEye, FaFileAlt, FaFilePdf, FaPen, FaSave } from "react-icons/fa";
import { SUBPROJECTS_CONSTANTS } from "../_MODULES/_API";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { ReadOnlyInfo, ReadOnlyModalShell } from "../../../../../_CORE/APP/Templates/_UI/ReadOnly/_index";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { ManualMarkdownEditor } from "../../_Manuals/_UI/ManualMarkdownEditor";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { downloadMarkdownDocument, downloadPdfDocument, safeDocumentName } from "../../../../_UI/DocumentExport";

export const PanelReadme = ({ subProject, onSaved }) => {

    const [useReadme, setReadme] = useState("");
    const [useSavedReadme, setSavedReadme] = useState("");
    const [useReaderOpen, setReaderOpen] = useState(false);
    const [useEditorOpen, setEditorOpen] = useState(false);
    const [useEditorMode, setEditorMode] = useState("split");

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 300,
    });

    useEffect(() => {
        const VALUE = subProject?.readme ?? "";
        setReadme(VALUE);
        setSavedReadme(VALUE);
    }, [subProject?.id, subProject?.readme]);

    const IS_DIRTY = useReadme !== useSavedReadme;

    const onSave = async ({ close = false } = {}) => {
        const RESPONSE = await actionMutate({
            method: "PATCH", endpoint: SUBPROJECTS_CONSTANTS.UPDATE, successMessage: "README actualizado", id: subProject.id,
            data: { readme: useReadme },
        });
        if (!RESPONSE.success) return false;
        setSavedReadme(useReadme);
        onSaved?.(RESPONSE.data ?? { id: subProject.id, readme: useReadme });
        if (close) setEditorOpen(false);
        return true;
    };

    const PREVIEW_VALUE = useMemo(() => useReadme?.trim() || "_Todavía no hay contenido. Abrí el editor para crear el README del subproyecto._", [useReadme]);

    const EXPORT_BASENAME = `${safeDocumentName(subProject?.name || "subproyecto")}-README`;
    const onExportMarkdown = () => downloadMarkdownDocument({
        title: `README · ${subProject?.name || "Subproyecto"}`,
        content: useReadme || "",
        fileName: `${EXPORT_BASENAME}.md`,
    });
    const onExportPdf = () => downloadPdfDocument({
        title: `README · ${subProject?.name || "Subproyecto"}`,
        content: useReadme || "",
        fileName: `${EXPORT_BASENAME}.pdf`,
    });
    const exportItems = [
        { key: "markdown", label: "Descargar Markdown (.md)", icon: <FaDownload />, onClick: onExportMarkdown },
        { key: "pdf", label: "Descargar PDF", icon: <FaFilePdf />, onClick: onExportPdf },
    ];

    const META = (
        <>
            <section className="LStudio__readonly_meta_section">
                <strong>Documento</strong>
                <h3>{subProject?.name || "Subproyecto"}</h3>
                <p>README renderizado desde Markdown, sin controles de edición.</p>
            </section>
            <section className="LStudio__readonly_meta_section">
                <ReadOnlyInfo
                    heading="README"
                    createdAt={subProject?.createdAt}
                    updatedAt={subProject?.updatedAt}
                    note="Este modal es solo lectura. Para modificar el README, abrí el editor desde la pestaña Readme."
                />
            </section>
        </>
    );

    return (
        <>
            <div className="LStudio__app_readme_editor_card LStudio__app_readme_compact_card">
                <div className="LStudio__app_panel_bar">
                    <div>
                        <span className="LStudio__app_panel_bar_title">README del subproyecto</span>
                        <p className="LStudio__app_readme_compact_hint">Vista rápida. El editor completo se abre en un modal amplio para trabajar sin apretar el contenido.</p>
                    </div>
                    <div className="LStudio__app_panel_bar_actions">
                        <CardActionMenu title="Opciones" ariaLabel="Opciones del README" items={exportItems} />
                        <AppButton variant="outline" tone="neutral" icon={<FaEye />} onClick={() => setReaderOpen(true)}>
                            Modo lectura
                        </AppButton>
                        <AppButton icon={<FaPen />} onClick={() => setEditorOpen(true)}>Editar README</AppButton>
                    </div>
                </div>

                <div className="LStudio__app_readme_compact_preview">
                    <ManualMarkdownEditor value={PREVIEW_VALUE} readOnly minHeight={230} ariaLabel="Vista previa del README" />
                </div>
            </div>

            <AppModal
                isOpen={useEditorOpen}
                onClose={() => setEditorOpen(false)}
                isDirty={IS_DIRTY}
                size="full"
                className="LStudio__app_readme_editor_modal"
                title={`Editar README · ${subProject?.name || "Subproyecto"}`}
                description="Editor Markdown completo. Podés alternar entre escribir, vista previa o dividido sin perder espacio de trabajo."
                footer={
                    <>
                        <CardActionMenu title="Opciones" ariaLabel="Opciones del README" items={[
                            { key: "save", label: "Guardar ahora", icon: <FaSave />, isDisabled: !IS_DIRTY, onClick: () => onSave() },
                            { divider: true },
                            ...exportItems,
                        ]} />
                        <AppButton variant="ghost" tone="neutral" onClick={() => setEditorOpen(false)}>Cancelar</AppButton>
                        <AppButton icon={<FaSave />} isLoading={useActionApiIsLoading} onClick={() => onSave({ close: true })}>
                            Guardar README
                        </AppButton>
                    </>
                }
            >
                <div className="LStudio__app_readme_markdown_field" data-field="readme">
                    <div className="LStudio__app_readme_markdown_label">
                        <strong>Contenido Markdown</strong>
                        <span>Usá la barra para títulos, viñetas, imágenes, tablas, enlaces, checklist, callouts y código.</span>
                    </div>
                    <ManualMarkdownEditor
                        value={useReadme}
                        onChange={setReadme}
                        mode={useEditorMode}
                        onModeChange={setEditorMode}
                        onSave={() => onSave()}
                        minHeight={620}
                        placeholder={"# Proyecto\n\n## Cómo levantarlo\n\n1. npm install\n2. npm start\n\n- [ ] Pendiente\n\n![Captura](https://...)"}
                        ariaLabel="README del subproyecto"
                    />
                </div>
            </AppModal>

            <ReadOnlyModalShell
                isOpen={useReaderOpen}
                onClose={() => setReaderOpen(false)}
                icon={<FaFileAlt />}
                title={subProject?.name || "Subproyecto"}
                currentLabel="README"
                meta={META}
                actions={<CardActionMenu title="Opciones" ariaLabel="Opciones del README" items={exportItems} />}
                className="LStudio__readme_reader_modal"
            >
                <article className="LStudio__readonly_document">
                    <div className="LStudio__readonly_document_title">
                        <span><FaFileAlt /></span>
                        <div>
                            <small>Documentación del subproyecto</small>
                            <h1>README</h1>
                        </div>
                    </div>
                    <div className="LStudio__readme_reader_markdown"><ManualMarkdownEditor value={PREVIEW_VALUE} readOnly minHeight={560} ariaLabel="README renderizado" /></div>
                </article>
            </ReadOnlyModalShell>
        </>
    );
};
